import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { PlatformFeatureType } from '@conversed/shared/src/central/types/platform-features.type';
import {
  StudioType,
  TRAIN_AI_STUDIO_TYPES,
} from '@conversed/shared/src/central/types/studio.type';
import { lazyNamed } from '@helpers/lazyNamed';
import { lazy } from 'react';
import {
  ACCOUNT_PAGE_ROUTE,
  ACTIVITY_LOGS_PAGE_ROUTE,
  AI_KNOWLEDGE_HUB_PAGE_ROUTE,
  AI_TICKET_DEFLECTION_ROUTE,
  ARTICLE_ARCHITECT_CONFLICTS_ROUTE,
  ARTICLE_ARCHITECT_GENERATION_ROUTE,
  ARTICLE_ARCHITECT_PAGE_ROUTE,
  ARTICLE_ARCHITECT_REVIEW_ROUTE,
  CHAT_PAGE_ROUTE,
  CONFIGURATION_PAGE_ROUTE,
  COOKIE_POLICY_ROUTE,
  DASHBOARD_PAGE_ROUTE,
  DESIGNER_PAGE_ROUTE,
  EDIT_APPEARANCE_PAGE_ROUTE,
  EXPORTS_PAGE_ROUTE,
  GDPR_PAGE_ROUTE,
  HOME_PAGE_ROUTE,
  INTEGRATIONS_PAGE_ROUTE,
  LOGOUT_PAGE_ROUTE,
  PREVIEW_PAGE_ROUTE,
  PRIVACY_POLICY_ROUTE,
  REPORTS_PAGE_ROUTE,
  REVISIONS_PAGE_ROUTE,
  SETTINGS_PAGE_ROUTE,
  TEMPLATE_RESPONSES_PAGE_ROUTE,
  TERMS_AND_CONDITIONS_ROUTE,
  TEST_ACCURACY_PAGE_ROUTE,
  TRAIN_AI_PAGE_ROUTE,
  VOICE_AI_PAGE_ROUTE,
} from '../../constants/routes';
import type { IRoute } from '../../interfaces/IRoutes';

const ConflictResolutionPage = lazyNamed(
  () => import('@components/article-architect/ConflictResolutionPage'),
  'ConflictResolutionPage',
);
const AiTicketDeflectionPage = lazy(
  () => import('@pages/AiTicketDeflectionPage'),
);
const ArticleArchitectPage = lazyNamed(
  () => import('@pages/ArticleArchitectPage'),
  'ArticleArchitectPage',
);
const ConfigurationPage = lazyNamed(
  () => import('@pages/ConfigurationPage'),
  'ConfigurationPage',
);
const CookiePolicyPage = lazyNamed(
  () => import('@pages/CookiePolicyPage'),
  'CookiePolicyPage',
);
const DashboardPage = lazyNamed(
  () => import('@pages/DashboardPage'),
  'DashboardPage',
);
const DataSourcesPage = lazyNamed(
  () => import('@pages/DataSourcesPage'),
  'DataSourcesPage',
);
const EditAppearancePage = lazyNamed(
  () => import('@pages/EditAppearancePage'),
  'EditAppearancePage',
);
const HomePage = lazyNamed(() => import('@pages/HomePage'), 'HomePage');
const IntegrationsPage = lazyNamed(
  () => import('@pages/IntegrationsPage'),
  'IntegrationsPage',
);
const PreviewPage = lazyNamed(
  () => import('@pages/PreviewPage'),
  'PreviewPage',
);
const ReviewQueuePage = lazyNamed(
  () => import('@pages/ReviewQueuePage'),
  'ReviewQueuePage',
);
const TemplateResponsesPage = lazyNamed(
  () => import('@pages/TemplateResponsesPage'),
  'TemplateResponsesPage',
);
const TestAccuracyPage = lazy(() => import('@pages/TestAccuracyPage'));
const TopdeskGenerationProgressPage = lazyNamed(
  () => import('@pages/TopdeskGenerationProgressPage'),
  'TopdeskGenerationProgressPage',
);
const TrainAIPage = lazyNamed(
  () => import('@pages/TrainAIPage'),
  'TrainAIPage',
);
const VoiceAIPage = lazyNamed(
  () => import('@pages/VoiceAIPage'),
  'VoiceAIPage',
);
const VoicePreviewPage = lazyNamed(
  () => import('@pages/voice/VoicePreviewPage'),
  'VoicePreviewPage',
);
const AccountPage = lazyNamed(
  () => import('../pages/AccountPage'),
  'AccountPage',
);
const ActivityLogsPage = lazyNamed(
  () => import('../pages/ActivityLogsPage'),
  'ActivityLogsPage',
);
const ChatPage = lazyNamed(
  () => import('../pages/chat/desktop/ChatPage'),
  'ChatPage',
);
const ChatInfoPage = lazyNamed(
  () => import('../pages/chat/mobile/ChatInfoPage'),
  'ChatInfoPage',
);
const ChatListPage = lazyNamed(
  () => import('../pages/chat/mobile/ChatListPage'),
  'ChatListPage',
);
const ChatMessagesPage = lazyNamed(
  () => import('../pages/chat/mobile/ChatMessagesPage'),
  'ChatMessagesPage',
);
const DesignerPage = lazyNamed(
  () => import('../pages/DesignerPage'),
  'DesignerPage',
);
const ExportsPage = lazyNamed(
  () => import('../pages/ExportsPage'),
  'ExportsPage',
);
const GDPRPage = lazyNamed(() => import('../pages/GDPRPage'), 'GDPRPage');
const LogoutPage = lazyNamed(() => import('../pages/LogoutPage'), 'LogoutPage');
const PrivacyPolicyPage = lazyNamed(
  () => import('../pages/PrivacyPolicyPage'),
  'PrivacyPolicyPage',
);
const ReportingPage = lazyNamed(
  () => import('../pages/ReportingPage'),
  'ReportingPage',
);
const RevisionsPage = lazyNamed(
  () => import('../pages/RevisionsPage'),
  'RevisionsPage',
);
const SettingsPage = lazyNamed(
  () => import('../pages/SettingsPage'),
  'SettingsPage',
);
const TermsAndConditionsPage = lazyNamed(
  () => import('../pages/TermsAndConditionsPage'),
  'TermsAndConditionsPage',
);

export const AuthenticatedAppRoutes = (isMobile: boolean): IRoute[] => [
  {
    path: LOGOUT_PAGE_ROUTE,
    component: LogoutPage,
    exact: true,
    studioTypes: [], // Available in all
  },
  {
    path: HOME_PAGE_ROUTE,
    component: HomePage,
    type: 'restricted',
    exact: true,
    permissions: [],
    studioTypes: [],
  },
  {
    path: DASHBOARD_PAGE_ROUTE,
    component: DashboardPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.DASHBOARD.DASHBOARD_VIEW],
    studioTypes: [],
    hiddenWhenFeatureActive: PlatformFeatureType.HomeV2,
  },
  {
    path: PREVIEW_PAGE_ROUTE,
    component: PreviewPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.WEBCLIENT.WEBCLIENT_PREVIEW],
    studioTypes: [
      StudioType.CHAT_AI,
      StudioType.TICKET_AI,
      StudioType.EMAIL_AI,
    ],
  },
  {
    path: PREVIEW_PAGE_ROUTE,
    component: VoicePreviewPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.VOICE_AI.VOICE_AI_PREVIEW],
    studioTypes: [StudioType.VOICE_AI],
  },
  {
    path: ARTICLE_ARCHITECT_CONFLICTS_ROUTE,
    component: ConflictResolutionPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    studioTypes: [],
  },
  {
    path: ARTICLE_ARCHITECT_REVIEW_ROUTE,
    component: ReviewQueuePage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_GENERATE],
    studioTypes: [],
  },
  {
    path: ARTICLE_ARCHITECT_GENERATION_ROUTE,
    component: TopdeskGenerationProgressPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    studioTypes: [],
  },
  {
    path: ARTICLE_ARCHITECT_PAGE_ROUTE,
    component: ArticleArchitectPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    studioTypes: [],
  },
  {
    path: AI_KNOWLEDGE_HUB_PAGE_ROUTE,
    component: DataSourcesPage,
    type: 'restricted',
    permissions: [PERMISSIONS.DATA_SOURCE.DATA_SOURCE_LIST],
    studioTypes: [],
  },
  {
    path: CONFIGURATION_PAGE_ROUTE,
    component: ConfigurationPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.WEBCLIENT.WEBCLIENT_CONFIG_LIST],
    studioTypes: [],
  },
  {
    path: TEMPLATE_RESPONSES_PAGE_ROUTE,
    component: TemplateResponsesPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.TEMPLATE_RESPONSE.TEMPLATE_RESPONSE_LIST],
    studioTypes: TRAIN_AI_STUDIO_TYPES,
  },
  {
    path: PRIVACY_POLICY_ROUTE,
    component: PrivacyPolicyPage,
    exact: true,
    studioTypes: [],
  },
  {
    path: COOKIE_POLICY_ROUTE,
    component: CookiePolicyPage,
    exact: true,
    studioTypes: [],
  },
  {
    path: TERMS_AND_CONDITIONS_ROUTE,
    component: TermsAndConditionsPage,
    exact: true,
    studioTypes: [],
  },
  {
    path: DESIGNER_PAGE_ROUTE,
    component: DesignerPage,
    exact: true,
    type: 'restricted',
    permissions: [PERMISSIONS.FLOW.FLOW_LIST],
    studioTypes: [
      StudioType.CHAT_AI,
      StudioType.EMAIL_AI,
      StudioType.TICKET_AI,
    ],
  },
  {
    path: REVISIONS_PAGE_ROUTE,
    component: RevisionsPage,
    exact: true,
    type: 'restricted',
    permissions: [],
    studioTypes: [
      StudioType.CHAT_AI,
      StudioType.EMAIL_AI,
      StudioType.TICKET_AI,
    ],
  },
  {
    path: EXPORTS_PAGE_ROUTE,
    component: ExportsPage,
    type: 'restricted',
    permissions: [PERMISSIONS.EXPORTS.EXPORT_LIST],
    studioTypes: [StudioType.CHAT_AI],
  },
  {
    path: GDPR_PAGE_ROUTE,
    component: GDPRPage,
    exact: true,
    type: 'restricted',
    permissions: [PERMISSIONS.GDPR.GDPR_LIST],
    studioTypes: [StudioType.CHAT_AI],
  },
  {
    path: REPORTS_PAGE_ROUTE,
    component: ReportingPage,
    exact: true,
    type: 'restricted',
    permissions: [PERMISSIONS.REPORTING.REPORTING_VIEW],
    studioTypes: [StudioType.CHAT_AI],
  },
  {
    path: TRAIN_AI_PAGE_ROUTE,
    component: TrainAIPage,
    type: 'restricted',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_VIEW],
    studioTypes: TRAIN_AI_STUDIO_TYPES,
  },
  {
    path: ACTIVITY_LOGS_PAGE_ROUTE,
    component: ActivityLogsPage,
    exact: true,
    type: 'restricted',
    permissions: [PERMISSIONS.LOGS.VIEW_LOGS],
    studioTypes: [StudioType.CHAT_AI],
  },
  {
    path: INTEGRATIONS_PAGE_ROUTE,
    component: IntegrationsPage,
    exact: true,
    studioTypes: [],
  },
  {
    path: EDIT_APPEARANCE_PAGE_ROUTE,
    component: EditAppearancePage,
    type: 'restricted',
    exact: true,
    permissions: [
      PERMISSIONS.WEBCLIENT.WEBCLIENT_CONFIG_LIST,
      PERMISSIONS.WEBCLIENT.WEBCLIENT_CONFIG_EDIT,
    ],
    studioTypes: [
      StudioType.CHAT_AI,
      StudioType.EMAIL_AI,
      StudioType.TICKET_AI,
    ],
  },
  {
    path: TEST_ACCURACY_PAGE_ROUTE,
    component: TestAccuracyPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.TEST_ACCURACY.TEST_ACCURACY_EDIT],
    studioTypes: [
      StudioType.CHAT_AI,
      StudioType.EMAIL_AI,
      StudioType.TICKET_AI,
    ],
  },
  {
    path: VOICE_AI_PAGE_ROUTE,
    component: VoiceAIPage,
    type: 'restricted',
    exact: true,
    permissions: [PERMISSIONS.VOICE_AI.VOICE_AI_CONFIGURATION_FORM],
    studioTypes: [],
  },
  {
    path: AI_TICKET_DEFLECTION_ROUTE,
    component: AiTicketDeflectionPage,
    type: 'restricted',
    exact: true,
    permissions: [
      PERMISSIONS.TICKET_DEFLECTION.TICKET_DEFLECTION_SUBMIT,
      PERMISSIONS.TICKET_DEFLECTION.TICKET_DEFLECTION_FEEDBACK,
    ],
    studioTypes: [],
  },

  ...(isMobile ? mobileOnlyRoutes : desktopOnlyRoutes),
];

const mobileOnlyRoutes: IRoute[] = [
  {
    path: `${CHAT_PAGE_ROUTE}`,
    component: ChatListPage,
    exact: true,
    type: 'restricted',
    permissions: [PERMISSIONS.CONVERSATION.CONVERSATION_MANAGE],
    studioTypes: [StudioType.CHAT_AI],
  },
  {
    path: `${CHAT_PAGE_ROUTE}/:id/info`,
    component: ChatInfoPage,
    exact: true,
    type: 'restricted',
    permissions: [PERMISSIONS.CONVERSATION.CONVERSATION_MANAGE],
    studioTypes: [StudioType.CHAT_AI],
  },
  {
    path: `${CHAT_PAGE_ROUTE}/:id`,
    component: ChatMessagesPage,
    exact: true,
    type: 'restricted',
    permissions: [PERMISSIONS.CONVERSATION.CONVERSATION_MANAGE],
    studioTypes: [StudioType.CHAT_AI],
  },
  {
    path: SETTINGS_PAGE_ROUTE,
    component: SettingsPage,
    exact: true,
    studioTypes: [],
    superAdminOnly: true,
  },
  {
    path: ACCOUNT_PAGE_ROUTE,
    component: AccountPage,
    exact: true,
    studioTypes: [],
  },
];

const desktopOnlyRoutes: IRoute[] = [
  {
    path: `${CHAT_PAGE_ROUTE}/:id?`,
    component: ChatPage,
    type: 'restricted',
    permissions: [PERMISSIONS.CONVERSATION.CONVERSATION_MANAGE],
    studioTypes: [StudioType.CHAT_AI],
  },
  {
    path: SETTINGS_PAGE_ROUTE,
    component: SettingsPage,
    exact: false,
    studioTypes: [],
    superAdminOnly: true,
  },
  {
    path: ACCOUNT_PAGE_ROUTE,
    component: AccountPage,
    exact: true,
    studioTypes: [],
  },
];
